// Folds partner incentive-campaign payouts into the reward stream the UI draws.
//
// The API reports incentives separately from drip rewards: they are absent from
// `history` and from `totalDistributedRawByMint`. The two streams are disjoint,
// so merging is a concat — no payout is counted twice. Left unmerged, incentive
// payouts are invisible everywhere: bars, totals and the asset selector all
// derive from those two fields.
//
// Kept dependency-free so it can be unit-tested without Solana/RPC.

import {
  DripHistoryItem,
  DripIncentiveCampaign,
} from "../../hooks/useDripHistory";

export type DripIncentives = Record<string, DripIncentiveCampaign>;

/** Raw amounts are token base units and can exceed 2^53, so sum them as BigInt. */
const addRaw = (a: string, b: string): string => {
  try {
    return (BigInt(a || "0") + BigInt(b || "0")).toString();
  } catch {
    return a || b || "0";
  }
};

/**
 * Campaign payouts as history items, so they land in the daily bars alongside
 * drip rewards. `campaignId:epoch` is unique per payout and namespaced away
 * from the API's own `exec:` keys.
 */
export const incentivePayoutsToHistory = (
  incentives: DripIncentives | undefined
): DripHistoryItem[] => {
  if (!incentives) return [];

  return Object.values(incentives).flatMap((campaign) =>
    (campaign.history ?? []).map((payout) => ({
      executionKey: `incentive:${payout.campaignId}:${payout.epoch}`,
      timestamp: payout.timestamp,
      mint: payout.mint,
      amount: payout.amount,
    }))
  );
};

/**
 * Adds each campaign's received total to the per-mint totals.
 *
 * Prefers `totalReceivedRaw` over re-summing the payout history: it is the
 * authoritative figure and covers anything the history does not itemise.
 * `pendingRaw` is deliberately excluded — it is accrued, not received, and the
 * totals line is what has actually landed.
 */
export const mergeIncentiveTotals = (
  totalsByMint: Record<string, string>,
  incentives: DripIncentives | undefined
): Record<string, string> => {
  if (!incentives) return totalsByMint;

  const merged = { ...totalsByMint };

  for (const campaign of Object.values(incentives)) {
    const received =
      campaign.totalReceivedRaw ??
      (campaign.history ?? []).reduce(
        (sum, payout) => addRaw(sum, payout.amount),
        "0"
      );

    merged[campaign.mint] = addRaw(merged[campaign.mint] ?? "0", received);
  }

  return merged;
};

export interface DripRewardTotalSplit {
  /** Rewards from the drip itself — the total less the incentive leg. */
  dripRaw: string;
  /** Rewards from incentive campaigns. */
  incentiveRaw: string;
}

/**
 * Breaks a merged total back into its two legs, for showing what came from
 * where. Clamps the drip leg at zero rather than reporting a negative, which
 * would mean the incentive total outran the merged total it is part of.
 */
export const splitRewardTotal = (
  totalRaw: string | undefined,
  incentiveRaw: string | undefined
): DripRewardTotalSplit => {
  try {
    const zero = BigInt(0);
    const total = BigInt(totalRaw || "0");
    const incentive = BigInt(incentiveRaw || "0");
    const drip = total - incentive;

    return {
      // `0n` literals need an ES2020 target; this project builds below it.
      dripRaw: (drip > zero ? drip : zero).toString(),
      incentiveRaw: incentive.toString(),
    };
  } catch {
    return { dripRaw: totalRaw || "0", incentiveRaw: "0" };
  }
};

/**
 * Decimals each campaign declares for its reward mint.
 *
 * Used as a fallback while the on-chain lookup is in flight or has failed: the
 * chart's own fallback is 9, which would render a 6-decimal token 1000x too
 * small.
 */
export const incentiveDecimalsByMint = (
  incentives: DripIncentives | undefined
): Record<string, number> => {
  if (!incentives) return {};

  const decimals: Record<string, number> = {};

  for (const campaign of Object.values(incentives)) {
    if (typeof campaign.decimals === "number") {
      decimals[campaign.mint] = campaign.decimals;
    }
  }

  return decimals;
};
